/**
 * StepCard — visual representation of a single step in the canvas.
 *
 * Reads:
 *   - step name + category controls (top row)
 *   - WHEN: ConditionDropZone (guard rows)
 *   - THEN: ActionsDropZone (action rows)
 *   - Cooldown (hours) + delete (footer)
 *
 * Hands every mutation back to the parent {@link VisualBuilder}, which
 * owns the canonical step list (so dirty tracking flows through one place).
 */

import type { StepCategory } from '@mana/shared';
import { Badge } from '../../../ui/Card.js';
import { Button } from '../../../ui/Button.js';
import { ConditionDropZone } from './ConditionDropZone.js';
import { ActionsDropZone } from './ActionsDropZone.js';
import type { ActionCatalogRecipient } from './ActionRow.js';
import type {
  VisualActionRow,
  VisualConditionGroup,
  VisualConditionRow,
  VisualStep,
} from './serialize.js';
import { makeEmptyActionRow, makeEmptyGroup } from './serialize.js';

const CATEGORY_OPTIONS: StepCategory[] = ['NOTIFY', 'WAIT', 'BRANCH', 'TASK', 'TERMINAL'];

export interface StepCardProps {
  step: VisualStep;
  index: number;
  total: number;
  readOnly: boolean;
  emailRecipients: readonly ActionCatalogRecipient[];
  phoneRecipients: readonly ActionCatalogRecipient[];
  onPatch: (next: VisualStep) => void;
  onRemove: () => void;
}

export function StepCard({
  step,
  index,
  total,
  readOnly,
  emailRecipients,
  phoneRecipients,
  onPatch,
  onRemove,
}: StepCardProps) {
  // ---- condition group helpers -----------------------------------------
  const mapGroups = (fn: (g: VisualConditionGroup) => VisualConditionGroup) =>
    onPatch({ ...step, conditionGroups: step.conditionGroups.map(fn) });

  const changeGroupInnerLogic = (groupId: string, logic: 'and' | 'or') =>
    mapGroups((g) => (g.id === groupId ? { ...g, logic } : g));

  const patchConditionRow = (groupId: string, rowId: string, next: VisualConditionRow) =>
    mapGroups((g) =>
      g.id === groupId
        ? { ...g, rows: g.rows.map((r) => (r.id === rowId ? next : r)) }
        : g,
    );

  const removeConditionRow = (groupId: string, rowId: string) =>
    mapGroups((g) =>
      g.id === groupId ? { ...g, rows: g.rows.filter((r) => r.id !== rowId) } : g,
    );

  const addGroup = () =>
    onPatch({ ...step, conditionGroups: [...step.conditionGroups, makeEmptyGroup()] });

  const removeGroup = (groupId: string) => {
    const remaining = step.conditionGroups.filter((g) => g.id !== groupId);
    onPatch({
      ...step,
      // Never leave the step with zero groups — keep one empty group so the
      // WHEN zone always has a drop target.
      conditionGroups: remaining.length > 0 ? remaining : [makeEmptyGroup()],
    });
  };

  const patchActionRow = (rowId: string, next: VisualActionRow) => {
    onPatch({
      ...step,
      actionRows: step.actionRows.map((r) => (r.id === rowId ? next : r)),
    });
  };
  const removeActionRow = (rowId: string) => {
    onPatch({
      ...step,
      actionRows: step.actionRows.filter((r) => r.id !== rowId),
    });
  };

  return (
    <article className="rounded-2xl border border-slate-200 bg-white shadow-card">
      {/* Header --------------------------------------------------------------- */}
      <header className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
        <span className="font-display text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
          Step {index + 1} of {total}
        </span>
        <input
          type="text"
          value={step.name}
          onChange={(e) => onPatch({ ...step, name: e.target.value })}
          placeholder="Step name"
          disabled={readOnly}
          className="flex-1 rounded-md border border-transparent px-2 py-1 text-base font-semibold text-brand-navy-800 hover:border-slate-200 focus:border-brand-navy-300 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
        />
        <select
          value={step.category}
          onChange={(e) => onPatch({ ...step, category: e.target.value as StepCategory })}
          disabled={readOnly}
          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-brand-navy-700 focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <Badge tone={step.isTerminal ? 'warning' : 'neutral'}>
          {step.isTerminal ? 'Terminal' : 'Continues'}
        </Badge>
        <button
          type="button"
          disabled={readOnly}
          onClick={onRemove}
          title="Delete step"
          className="ml-1 rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={`Delete step ${index + 1}`}
        >
          ✕
        </button>
      </header>

      {/* WHEN clause ---------------------------------------------------------- */}
      <section className="border-b border-slate-100 px-4 py-3">
        <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-navy-600">
          When
        </h4>
        <ConditionDropZone
          stepKey={step.key}
          groupLogic={step.groupLogic}
          groups={step.conditionGroups}
          readOnly={readOnly}
          unsupported={step.unsupportedGuard}
          onChangeGroupLogic={(logic) => onPatch({ ...step, groupLogic: logic })}
          onChangeGroupInnerLogic={changeGroupInnerLogic}
          onChangeRow={patchConditionRow}
          onRemoveRow={removeConditionRow}
          onAddGroup={addGroup}
          onRemoveGroup={removeGroup}
        />
      </section>

      {/* THEN clause ---------------------------------------------------------- */}
      <section className="border-b border-slate-100 px-4 py-3">
        <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-navy-600">
          Then
        </h4>
        <ActionsDropZone
          stepKey={step.key}
          rows={step.actionRows}
          readOnly={readOnly}
          emailRecipients={emailRecipients}
          phoneRecipients={phoneRecipients}
          onAdd={() =>
            onPatch({ ...step, actionRows: [...step.actionRows, makeEmptyActionRow()] })
          }
          onChangeRow={patchActionRow}
          onRemoveRow={removeActionRow}
        />
      </section>

      {/* Footer --------------------------------------------------------------- */}
      <footer className="flex flex-wrap items-center gap-3 px-4 py-3 text-xs">
        <label className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Cooldown (hrs)
          </span>
          <input
            type="number"
            min={0}
            max={720}
            value={step.cooldownHours}
            onChange={(e) =>
              onPatch({
                ...step,
                cooldownHours: Math.max(0, Math.min(720, parseInt(e.target.value, 10) || 0)),
              })
            }
            disabled={readOnly}
            className="w-20 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200 disabled:bg-slate-50"
          />
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={step.isTerminal}
            onChange={(e) => onPatch({ ...step, isTerminal: e.target.checked })}
            disabled={readOnly}
            className="h-3.5 w-3.5 rounded border-slate-300 text-brand-navy-600 focus:ring-brand-navy-300"
          />
          <span className="text-slate-700">Workflow ends after this step</span>
        </label>
        <span className="ml-auto text-[10px] text-slate-400">
          Key{' '}
          <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[10px]">
            {step.key}
          </code>
        </span>
      </footer>
    </article>
  );
}

export interface AddStepButtonProps {
  disabled: boolean;
  onAdd: () => void;
}

export function AddStepButton({ disabled, onAdd }: AddStepButtonProps) {
  return (
    <div className="flex justify-center">
      <Button variant="gold" size="sm" disabled={disabled} onClick={onAdd}>
        + Add step
      </Button>
    </div>
  );
}
